import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useToast } from '../context/ToastContext';
import {
  useGetFaqQuery,
  useDeleteFaqMutation,
  useGetFaqCategoryQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useGetFaqCategoriesQuery,
  useReloadRagMutation,
} from '../redux/services/faqApi';

const Faq = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reloadDialogOpen, setReloadDialogOpen] = useState(false);
  
  // Convert categoryId to number or default to 0
  const numericCategoryId = categoryId ? parseInt(categoryId, 10) : 0;
  
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category_id: numericCategoryId // Initialize with numeric value
  });

  const {
    data: faqItems,
    isLoading: isFaqLoading,
    error: faqError,
    refetch,
  } = useGetFaqQuery(categoryId);

  const {
    data: categoryResponse,
    isLoading: isCategoryLoading,
  } = useGetFaqCategoryQuery(categoryId, {
    skip: !categoryId,
  });

  // Add this query to fetch all categories when viewing all FAQs
  const {
    data: allCategories,
    isLoading: isAllCategoriesLoading,
  } = useGetFaqCategoriesQuery(undefined, {
    skip: !!categoryId, // Only fetch all categories when not viewing a specific category
  });

  const category = categoryResponse?.item;

  const [deleteFaq] = useDeleteFaqMutation();
  const [createFaq, { isLoading: isCreating }] = useCreateFaqMutation();
  const [updateFaq, { isLoading: isUpdating }] = useUpdateFaqMutation();
  const [reloadRag, { isLoading: isReloading }] = useReloadRagMutation();

  // Safely extract the array of FAQs from the API response
  const faqArray = useMemo(() => {
    if (!faqItems) return [];
    return faqItems || [];
  }, [faqItems]);

  // Now use faqArray instead of faqItems for filtering
  const filteredFaqs = faqArray.filter(
    faq => {
      return faq.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              faq.answer?.toLowerCase().includes(searchQuery.toLowerCase())
    }
  );

  // Create a lookup object for category names
  const categoryLookup = useMemo(() => {
    if (!allCategories || categoryId) return {};
    
    // Handle different possible data structures for categories
    const categoriesArray = Array.isArray(allCategories) 
      ? allCategories 
      : (allCategories.items || []);
    
    return categoriesArray.reduce((lookup, cat) => {
      lookup[cat.id] = cat.name;
      return lookup;
    }, {});
  }, [allCategories, categoryId]);

  const handleDeleteClick = (faq) => {
    setSelectedFaq(faq);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteFaq(selectedFaq.id).unwrap();
      showToast('FAQ deleted successfully', 'success');
      refetch();
    } catch (err) {
      showToast(
        err.data?.message || 'Failed to delete FAQ', 
        'error'
      );
    } finally {
      setDeleteDialogOpen(false);
      setSelectedFaq(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFaqForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditClick = (faq) => {
    setSelectedFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      // Ensure category_id is a number
      category_id: faq.category_id ? parseInt(faq.category_id, 10) : 0
    });
    setEditDialogOpen(true);
  };

  const handleCreateClick = () => {
    // Reset the form to empty values
    setFaqForm({
      question: '',
      answer: '',
      category_id: numericCategoryId
    });
    setCreateDialogOpen(true);
  };

  const handleCreateFaq = async () => {
    try {
      // Prepare data for API - ensure category_id is a number
      const faqData = {
        question: faqForm.question,
        answer: faqForm.answer,
        category_id: faqForm.category_id ? parseInt(faqForm.category_id, 10) : 0
      };
        
      // Call the createFaq mutation
      await createFaq(faqData).unwrap();
      
      // Show success message and reset form
      showToast('FAQ created successfully', 'success');
      setCreateDialogOpen(false);
      setFaqForm({
        question: '',
        answer: '',
        category_id: numericCategoryId
      });
      
      // Refetch to update the list
      refetch();
    } catch (error) {
      showToast(error.data?.message || 'Failed to create FAQ', 'error');
    }
  };

  const handleUpdateFaq = async () => {
    try {
      // Prepare data for API - ensure category_id is a number
      const faqData = {
        faqId: selectedFaq.id,
        question: faqForm.question,
        answer: faqForm.answer,
        category_id: faqForm.category_id ? parseInt(faqForm.category_id, 10) : 0
      };
      
      // Call the updateFaq mutation
      await updateFaq(faqData).unwrap();
      
      // Show success message
      showToast('FAQ updated successfully', 'success');
      setEditDialogOpen(false);
      
      // Refetch to update the list
      refetch();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      showToast(error.data?.message || 'Failed to update FAQ', 'error');
    }
  };

  const handleReloadRag = () => {
    setReloadDialogOpen(true);
  };

  const handleReloadConfirm = async () => {
    try {
      await reloadRag().unwrap();
      showToast('RAG system sent for reprocessing', 'success');
    } catch (error) {
      showToast(error.data?.message || 'Failed to reload RAG system', 'error');
    } finally {
      setReloadDialogOpen(false);
    }
  };

  const isLoading = isFaqLoading || isCategoryLoading || isCreating || isUpdating || isAllCategoriesLoading || isReloading;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (faqError) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading FAQs
      </Typography>
    );
  }

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/faq-categories')}
            sx={{ mr: 2 }}
          >
            Back to Categories
          </Button>
          <Typography variant="h4" component="h1">
            {category?.name ? `${category.name}` : 'All FAQ Items'}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          sx={{ flex: 1, maxWidth: 500 }}
          variant="outlined"
          size="small"
          placeholder="Search FAQs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReloadRag}
            disabled={isReloading}
          >
            {isReloading ? 'Reloading...' : 'Reload RAG'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Add FAQ
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {filteredFaqs.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No FAQs found {categoryId ? 'for this category' : ''}.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          {filteredFaqs.map((faq) => (
            <Accordion key={faq.id} sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`faq-${faq.id}-content`}
                id={`faq-${faq.id}-header`}
                sx={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pr: 2
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 'medium', display: 'inline' }}>
                    {faq.question}
                  </Typography>
                  {!categoryId && (
                    <Typography 
                      variant="body2"
                      color="primary.main"
                      sx={{ ml: 1, display: 'inline' }}
                    >
                      {faq.category_id && categoryLookup[faq.category_id] 
                        ? `[${categoryLookup[faq.category_id]}]` 
                        : '[No category]'}
                    </Typography>
                  )}
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    ml: 2,
                    alignItems: 'center',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(faq)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(faq)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" whiteSpace="pre-line">
                  {faq.answer}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Last updated: {new Date(faq.updated).toLocaleString()}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this FAQ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit FAQ Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit FAQ</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            name="question"
            fullWidth
            value={faqForm.question}
            onChange={handleInputChange}
            variant="outlined"
            multiline
            rows={8}
          />
          <TextField
            margin="dense"
            label="Answer"
            name="answer"
            fullWidth
            value={faqForm.answer}
            onChange={handleInputChange}
            variant="outlined"
            multiline
            rows={8}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateFaq} 
            color="primary" 
            variant="contained"
            disabled={!faqForm.question.trim() || !faqForm.answer.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create FAQ Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New FAQ</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            name="question"
            fullWidth
            value={faqForm.question}
            onChange={handleInputChange}
            variant="outlined"
            sx={{ mb: 2 }}
            multiline
            rows={8}
          />
          <TextField
            margin="dense"
            label="Answer"
            name="answer"
            fullWidth
            value={faqForm.answer}
            onChange={handleInputChange}
            variant="outlined"
            multiline
            rows={8}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleCreateFaq} 
            color="primary" 
            variant="contained"
            disabled={!faqForm.question.trim() || !faqForm.answer.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reload Confirmation Dialog */}
      <Dialog
        open={reloadDialogOpen}
        onClose={() => setReloadDialogOpen(false)}
      >
        <DialogTitle>
          Confirm Reload
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reload the RAG system? This may take a few moments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReloadDialogOpen(false)} 
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleReloadConfirm} 
            color="primary" 
            variant="contained"
            disabled={isReloading}
          >
            {isReloading ? 'Reloading...' : 'Reload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Faq; 